import React, { useState } from 'react';
import { useConfirm } from '../../context/ConfirmContext';
import { errorMessage, mergeDrafts, updateItemStatus } from '../../api/catalogImportApi';
import { btnPrimary, btnSmall, pill } from './styles';

const inr = (v) => (v === null || v === undefined ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);
const VERDICT = {
  VARIANT: { text: 'Same name, other size/colour', style: pill('#FEF3C7', '#92400E') },
  SAME: { text: 'Same product, shown twice', style: pill('#FEE2E2', '#B91C1C') },
  RELATED: { text: 'Possibly related', style: pill('#F3F4F6', '#4B5563') },
};

/**
 * "Related drafts in this catalog": other pages that look like options of this product
 * (300ml here, 700ml there) or the same product repeated — found by name rules and photo
 * matching, never AI. The reviewer chooses what to merge —
 * merged drafts become options on this one, which the reviewer then completes and checks.
 */
export default function SiblingPanel({ item, onItemChanged, onReload, onError }) {
  const confirm = useConfirm();
  const siblings = item.siblings || [];
  const [selected, setSelected] = useState(() => new Set(siblings.filter((s) => s.verdict === 'VARIANT').map((s) => s.itemId)));
  const [busy, setBusy] = useState(false);
  if (siblings.length === 0) return null;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const merge = async () => {
    const chosen = siblings.filter((s) => selected.has(s.itemId));
    const ok = await confirm({
      title: `Merge ${chosen.length} draft${chosen.length > 1 ? 's' : ''} into this one?`,
      message: `${chosen.map((s) => `"${s.name || `#${s.itemId}`}" (page ${s.pageNumber})`).join(', ')} will become options of "${item.name || 'this draft'}", with their photos. `
        + 'Check each option’s colour, size and price before creating. Merged drafts can’t be un-merged.',
      confirmLabel: 'Merge as options',
    });
    if (!ok) return;
    setBusy(true);
    try {
      onItemChanged((await mergeDrafts(item.id, chosen.map((s) => s.itemId))).data);
      onReload();
    } catch (err) {
      onError(errorMessage(err, 'Could not merge the drafts.'));
    } finally {
      setBusy(false);
    }
  };

  const discardCopy = async (s) => {
    const ok = await confirm({
      title: 'Discard the repeated copy?',
      message: `"${s.name || `#${s.itemId}`}" on page ${s.pageNumber} will be discarded; this draft is kept. You can restore it from the Discarded tab.`,
      confirmLabel: 'Discard copy', tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await updateItemStatus(s.itemId, 'DISCARDED');
      onReload();
    } catch (err) {
      onError(errorMessage(err, 'Could not discard the copy.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Related drafts in this catalog" style={{ border: '1.5px solid #FCD34D', background: '#FFFBEB', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <strong style={{ fontSize: 14, color: '#92400E' }}>Related drafts in this catalog</strong>
          <div style={{ fontSize: 12, color: '#92400E' }}>These look related by name or photo. Tick the ones to merge in as options.</div>
        </div>
        <button style={{ ...btnPrimary, opacity: busy || selected.size === 0 ? 0.6 : 1 }} disabled={busy || selected.size === 0} onClick={merge}>
          Merge {selected.size || ''} as options
        </button>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {siblings.map((s) => {
          const v = VERDICT[s.verdict] || VERDICT.RELATED;
          return (
            <div key={s.itemId} style={{ width: 210, background: '#fff', border: `1.5px solid ${selected.has(s.itemId) ? '#F29213' : '#E5E7EB'}`, borderRadius: 8, padding: 8, fontSize: 12 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.has(s.itemId)} onChange={() => toggle(s.itemId)}
                       style={{ width: 16, height: 16, accentColor: '#F29213', marginTop: 2 }} />
                <span>
                  <strong style={{ color: '#111827' }}>{s.name || `Draft #${s.itemId}`}</strong>
                  <span style={{ display: 'block', color: '#6B7280' }}>Page {s.pageNumber} · {inr(s.mrp)}</span>
                </span>
              </label>
              {s.imageUrl && <img src={s.imageUrl} alt={s.name || ''} style={{ width: '100%', height: 90, objectFit: 'contain', margin: '6px 0' }} />}
              <span style={v.style}>{v.text}</span>
              {s.reason && <div style={{ color: '#4B5563', fontStyle: 'italic', marginTop: 4 }}>“{s.reason}”</div>}
              {s.verdict === 'SAME' && (
                <button style={{ ...btnSmall, marginTop: 6, width: '100%' }} disabled={busy} onClick={() => discardCopy(s)}>
                  Discard that copy
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
